import React, { useState, useCallback, useMemo, useEffect } from "react";
import { MdClose, MdShoppingCart, MdExpandMore } from "react-icons/md";
import { FaPepperHot, FaPlus, FaMinus } from "react-icons/fa";
import { motion, AnimatePresence, LayoutGroup, useReducedMotion } from "framer-motion";
import clsx from 'clsx';
import { MenuItem } from "../redux/slices/menuSlice";
import {
  filterEnabledModifiers,
  handleModifierOptionSelection,
  isModifierOptionSelected,
  isModifierRequired,
  isModifierMultiSelect,
  getMaxOptionsToSelect,
} from "../utils/modifierUtils";
import { calculateTotalPrice, formatPrice } from "../utils/priceUtils";
import {
  shouldShowSpiceLevel,
  SPICE_LEVELS,
  DEFAULT_SPICE_LEVEL,
} from "../utils/spiceUtils";
import {
  validateAllSelections,
  type ValidationErrors,
} from "../utils/validationUtils";

interface ModifierOption {
  id: string | number;
  name: string;
  price: number | string;
}

interface SelectedModifier {
  name: string;
  options: ModifierOption[];
}

interface AddToCartModalProps {
  isOpen: boolean;
  item: MenuItem | null;
  onClose: () => void;
  onAddToCart: (
    item: MenuItem,
    quantity: number,
    spiceLevel: number | null,
    modifiers: SelectedModifier[]
  ) => Promise<void> | void;
  editMode?: boolean;
  existingCartItem?: any;
  onUpdateCart?: (
    cartItemId: string,
    item: MenuItem,
    quantity: number,
    spiceLevel: number | null,
    modifiers: SelectedModifier[]
  ) => Promise<void> | void;
}

export const AddToCartModal: React.FC<AddToCartModalProps> = React.memo(
  ({ isOpen, item, onClose, onAddToCart, editMode = false, existingCartItem, onUpdateCart }) => {
    const prefersReducedMotion = useReducedMotion();
    
    const [quantity, setQuantity] = useState(1);
    const [spiceLevel, setSpiceLevel] = useState(DEFAULT_SPICE_LEVEL);
    const [selectedModifiers, setSelectedModifiers] = useState<SelectedModifier[]>([]);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedModifiers, setExpandedModifiers] = useState<Set<string>>(new Set());

    // Animation transitions
    const modalTransition = prefersReducedMotion
      ? { duration: 0 }
      : { type: "spring" as const, stiffness: 260, damping: 25 };

    // Staggered option variants
    const optionVariants = {
      hidden: { opacity: 0, y: 6 },
      visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.03 },
      }),
    };

    // Modifiers list - Move before useEffect
    const modifiersList = useMemo(
      () => (item ? filterEnabledModifiers(item.modifiers_list || []) : []),
      [item?.modifiers_list]
    );

    // Get ALL required modifier names for auto-expand
    const requiredModifiers = useMemo(() => {
      return modifiersList
        .filter(mod => isModifierRequired(mod))
        .map(mod => mod.name);
    }, [modifiersList]);

    // Reset when modal opens or pre-populate for edit mode
    useEffect(() => {
      if (isOpen && item) {
        if (editMode && existingCartItem) {
          // Pre-populate with existing cart item data
          setQuantity(existingCartItem.quantity);
          setSpiceLevel(existingCartItem.selected.spice?.id || DEFAULT_SPICE_LEVEL);
          setSelectedModifiers(existingCartItem.selected.modifiers || []);
        } else {
          // Reset for new item
          setQuantity(1);
          setSpiceLevel(DEFAULT_SPICE_LEVEL);
          setSelectedModifiers([]);
        }
        setValidationErrors({});
        setIsAddingToCart(false);
        setError(null);
        // Auto-expand all required modifiers
        setExpandedModifiers(new Set(requiredModifiers));
      }
    }, [isOpen, item, editMode, existingCartItem, requiredModifiers]);

    // ESC key close + body scroll lock
    useEffect(() => {
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === "Escape" && isOpen) {
          onClose();
        }
      };
      if (isOpen) {
        document.addEventListener("keydown", handleEscKey);
        document.body.style.overflow = "hidden";
        return () => {
          document.removeEventListener("keydown", handleEscKey);
          document.body.style.overflow = "";
        };
      }
    }, [isOpen, onClose]);

    const handleModifierOptionSelect = useCallback(
      (modifierName: string, option: ModifierOption) => {
        setSelectedModifiers((prev) =>
          handleModifierOptionSelection(modifierName, option, prev, modifiersList)
        );
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[modifierName];
          return newErrors;
        });
      },
      [modifiersList]
    );

    const isModifierOptionSelectedLocal = useCallback(
      (modifierName: string, optionName: string): boolean =>
        isModifierOptionSelected(modifierName, optionName, selectedModifiers),
      [selectedModifiers]
    );

    const totalPrice = useMemo(
      () =>
        item ? calculateTotalPrice(item.price, selectedModifiers, quantity) : 0,
      [item, selectedModifiers, quantity]
    );

    const validateSelections = useCallback((): boolean => {
      if (!item) return false;
      const validation = validateAllSelections(
        item,
        modifiersList,
        selectedModifiers,
        spiceLevel
      );
      setValidationErrors(validation.errors);
      return validation.isValid;
    }, [item, modifiersList, selectedModifiers, spiceLevel]);

    const handleAddToCartClick = useCallback(async () => {
      if (!item || !validateSelections()) return;

      setIsAddingToCart(true);
      setError(null);

      try {
        const finalSpiceLevel = shouldShowSpiceLevel(item) ? spiceLevel : null;
        
        if (editMode && existingCartItem && onUpdateCart) {
          await onUpdateCart(existingCartItem.cartItemId, item, quantity, finalSpiceLevel, selectedModifiers);
        } else {
          await onAddToCart(item, quantity, finalSpiceLevel, selectedModifiers);
        }
        onClose();
      } catch (err) {
        console.error(editMode ? "Update order failed:" : "Add to order failed:", err);
        setError(editMode ? "Failed to update order item. Please try again." : "Failed to add item to order. Please try again.");
      } finally {
        setIsAddingToCart(false);
      }
    }, [
      item,
      quantity,
      spiceLevel,
      selectedModifiers,
      onAddToCart,
      onUpdateCart,
      editMode,
      existingCartItem,
      onClose,
      validateSelections,
    ]);

    const isFormValid = useMemo(() => {
      if (!item) return false;
      return validateAllSelections(
        item,
        modifiersList,
        selectedModifiers,
        spiceLevel
      ).isValid;
    }, [item, modifiersList, selectedModifiers, spiceLevel]);

    return (
      <AnimatePresence>
        {isOpen && item && (
          <motion.div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onClick={onClose}
          >
            <motion.div
              layout
              className="bg-white rounded-2xl shadow-xl w-full max-w-[95vw] sm:max-w-md md:max-w-lg lg:max-w-xl max-h-[90dvh] flex flex-col relative overflow-hidden"
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={modalTransition}
              onClick={(e) => e.stopPropagation()}
            >
          {/* Header */}
          <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-200 p-3 sm:p-4 md:p-5 flex items-center justify-between z-10">
            <h2
              id="modal-title"
              className="text-lg sm:text-xl font-bold text-gray-900"
            >
              {item.name}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <MdClose className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6" style={{ paddingBottom: '80px' }}>
            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3" role="alert" aria-live="polite">
                <p className="text-red-600 text-xs sm:text-sm font-medium">{error}</p>
              </div>
            )}

            {/* Spice Level */}
            {shouldShowSpiceLevel(item) && (
              <section>
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2 text-sm sm:text-base">
                  Spice Level
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FB2B37', color: 'white' }}>
                    Required
                  </span>
                </h4>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {SPICE_LEVELS.map((level) => (
                    <button
                      key={level.id}
                      onClick={() => setSpiceLevel(level.id)}
                      className={`p-2 sm:p-3 rounded-lg border text-center transition-colors ${
                        spiceLevel === level.id
                          ? "border-red-500 bg-red-200"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      aria-pressed={spiceLevel === level.id}
                    >
                      <div className="flex items-center justify-center">
                        {Array.from({ length: level.pepperCount }).map((_, i) => (
                          <FaPepperHot
                            key={i}
                            className={`h-4 w-4 sm:h-5 sm:w-5 mx-0.5 ${
                              spiceLevel === level.id ? "text-red-700" : "text-red-600"
                            }`}
                          />
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
                {validationErrors["Spice Level"] && (
                  <p
                    className="text-red-500 text-xs mt-1 font-medium"
                    role="alert"
                  >
                    {validationErrors["Spice Level"]}
                  </p>
                )}
              </section>
            )}

            {/* Modifiers */}
            <LayoutGroup>
              <motion.div layout className="space-y-4 mb-6">
                {modifiersList.map((modifier, index) => {
                  // Calculate max options and current selection count
                  const maxNum = getMaxOptionsToSelect(modifier.max_options_to_select);
                  const selectedCount = selectedModifiers.find(m => m.name === modifier.name)?.options.length || 0;
                  
                  return (
                  <motion.section 
                    key={modifier.name}
                    layout
                    className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden"
                  >
                {/* Collapsible Header */}
                <button
                  type="button"
                  onClick={() => setExpandedModifiers((prev) => {
                    const newSet = new Set(prev);
                    if (newSet.has(modifier.name)) {
                      newSet.delete(modifier.name);
                    } else {
                      newSet.add(modifier.name);
                    }
                    return newSet;
                  })}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-100 transition-colors text-left"
                >
                  <h4 className="font-medium text-gray-800 flex items-center gap-2 flex-wrap text-sm sm:text-base">
                    {modifier.name}
                    {isModifierRequired(modifier) ? (
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FB2B37', color: 'white' }}>
                        Required
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">(Optional)</span>
                    )}
                    <span className="text-xs text-gray-500">
                      • Select up to {!isModifierMultiSelect(modifier) ? 1 : maxNum !== Infinity ? maxNum : modifier.options.length}
                    </span>
                  </h4>
                  <MdExpandMore 
                    className={`h-5 w-5 text-gray-500 transition-transform duration-300 flex-shrink-0 ${
                      expandedModifiers.has(modifier.name) ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                {/* Collapsible Content */}
                <AnimatePresence initial={false}>
                  {expandedModifiers.has(modifier.name) && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{
                        height: { duration: 0.3, ease: "easeInOut" },
                        opacity: { duration: 0.2 },
                      }}
                      className="overflow-hidden px-4"
                    >
                      <div className="py-4 space-y-2">
                    {modifier.options.map((option: ModifierOption, optIndex: number) => {
                      // Check if this option should be disabled (max reached for unselected options)
                      const isMaxReached = isModifierMultiSelect(modifier) && 
                                           selectedCount >= maxNum && 
                                           !isModifierOptionSelectedLocal(modifier.name, option.name);
                      
                      return (
                      <motion.button
                        key={optIndex}
                        custom={optIndex}
                        variants={optionVariants}
                        initial="hidden"
                        animate="visible"
                        whileTap={{ scale: isMaxReached ? 1 : 0.97 }}
                        transition={{ type: "spring", stiffness: 400, damping: 20 }}
                        onClick={() => handleModifierOptionSelect(modifier.name, option)}
                        disabled={isMaxReached}
                        className={clsx(
                          'w-full p-3 min-h-[48px] rounded-lg border text-left transition-all',
                          'flex items-center justify-between',
                          isModifierOptionSelectedLocal(modifier.name, option.name)
                            ? 'border-2 bg-red-50'
                            : isMaxReached
                              ? 'bg-gray-100 border-gray-200 opacity-60 cursor-not-allowed'
                              : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        )}
                        style={{
                          borderColor: isModifierOptionSelectedLocal(modifier.name, option.name)
                            ? '#FB2B37'
                            : undefined,
                        }}
                        aria-pressed={isModifierOptionSelectedLocal(
                          modifier.name,
                          option.name
                        )}
                        aria-disabled={isMaxReached}
                      >
                        <div className="flex items-center">
                          <div className="mr-3">
                            {isModifierMultiSelect(modifier) ? (
                              <div
                                className={`w-4 h-4 border rounded ${
                                  isModifierOptionSelectedLocal(modifier.name, option.name)
                                    ? "bg-red-500 border-red-500"
                                    : "border-gray-400"
                                } flex items-center justify-center`}
                              >
                                {isModifierOptionSelectedLocal(modifier.name, option.name) && (
                                  <svg
                                    className="w-3 h-3 text-white"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                )}
                              </div>
                            ) : (
                              <div
                                className={`w-4 h-4 border rounded-full ${
                                  isModifierOptionSelectedLocal(modifier.name, option.name)
                                    ? "border-red-500"
                                    : "border-gray-400"
                                } flex items-center justify-center`}
                              >
                                {isModifierOptionSelectedLocal(modifier.name, option.name) && (
                                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                )}
                              </div>
                            )}
                          </div>
                          <span className="font-medium text-xs sm:text-sm">
                            {option.name}
                          </span>
                        </div>
                        <span className="text-xs text-gray-600 font-medium">
                          +{formatPrice(option.price)}
                        </span>
                      </motion.button>
                      );
                    })}
                    
                      {validationErrors[modifier.name] && (
                        <p
                          className="text-red-500 text-xs mt-2 font-medium"
                          role="alert"
                        >
                          {validationErrors[modifier.name]}
                        </p>
                      )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                </motion.section>
                  );
                })}
              </motion.div>
            </LayoutGroup>
          </div>

          {/* Sticky Footer with iOS Safe Area */}
          <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 sm:p-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-lg">
            <div className="flex items-center gap-3">
              {/* Quantity Controls */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-white flex items-center justify-center transition-all disabled:opacity-50"
                  aria-label="Decrease quantity"
                >
                  <FaMinus className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                </button>
                <span className="w-8 sm:w-10 text-center font-bold text-sm sm:text-base text-gray-900">{quantity}</span>
                <button
                  onClick={() => setQuantity(q => q + 1)}
                  className="w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-white flex items-center justify-center transition-all"
                  aria-label="Increase quantity"
                >
                  <FaPlus className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                </button>
              </div>

              {/* Add to Cart Button */}
              <motion.button
                onClick={handleAddToCartClick}
                disabled={isAddingToCart || !isFormValid}
                whileTap={{ scale: isAddingToCart || !isFormValid ? 1 : 0.97 }}
                whileHover={{ scale: isAddingToCart || !isFormValid ? 1 : 1.01 }}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
                className={`flex-1 font-bold py-2.5 sm:py-3 md:py-3.5 text-sm sm:text-base rounded-xl transition-all duration-200 flex items-center justify-center min-h-[48px] sm:min-h-[52px] ${
                  isAddingToCart || !isFormValid
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-red-500 hover:bg-red-600 text-white"
                }`}
                aria-label={`Add ${item.name} to order for ${formatPrice(totalPrice)}`}
              >
                {isAddingToCart ? (
                  <div className="flex items-center text-sm sm:text-base">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    {editMode ? "Updating..." : "Adding..."}
                  </div>
                ) : (
                  <>
                    <MdShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    <span className="text-sm sm:text-base font-bold">
                      {editMode ? `Update Order • ${formatPrice(totalPrice)}` : `Add to Order • ${formatPrice(totalPrice)}`}
                    </span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }
);

AddToCartModal.displayName = 'AddToCartModal';
